/**
 * Open a link outside the app, WITHOUT ever taking the current page with it.
 *
 * That last part is the whole job. This screen is a till: navigating it away
 * means the person who tapped a support number is now looking at WhatsApp
 * with no back button and no way to sign in. So there is no branch here that
 * touches `window.location` — if a link cannot be opened somewhere else, it
 * is not opened at all.
 *
 * Two ways, tried in order, because the same build runs in two places:
 *
 *   1. the packaged till — Electron's `shell.openExternal`, which hands the
 *      URL to Windows. Reachable through `window.require` because
 *      `public/main.js` builds its windows with `nodeIntegration: true` and
 *      no preload. If context isolation is ever turned on there, this simply
 *      stops matching and step 2 takes over.
 *   2. a browser, including `yarn start` — a new tab.
 *
 * ── The bug this file was rewritten for ──────────────────────────────────
 *
 * It used to open the tab with `window.open(url, '_blank', 'noopener')` and
 * treat a null return as failure. But `window.open` returns null WHENEVER
 * `noopener` is set — on success as much as on failure; the spec says so, and
 * it is the point of `noopener`, since the whole purpose is to hand back no
 * handle to the new window. So the check never passed, the fallback always
 * ran, and every tap opened WhatsApp in a new tab AND navigated the app out
 * of the old one.
 *
 * The fix keeps both the protection and a usable answer: open the tab
 * normally, so the return value means something, then sever `opener` on the
 * window that comes back. The new page still cannot reach into this one.
 */
export default function openExternalLink(url) {
  if (!url) return false;

  /* 1. Electron: let Windows open it, in WhatsApp Desktop or the browser. */
  try {
    const electron =
      typeof window !== 'undefined' && typeof window.require === 'function'
        ? window.require('electron')
        : null;

    if (electron && electron.shell && electron.shell.openExternal) {
      electron.shell.openExternal(url);
      return true;
    }
  } catch (err) {
    // Not Electron, or node integration is off. Fall through.
  }

  /* 2. A browser: a new tab, with no handle back to this one. */
  try {
    const opened = window.open(url, '_blank');
    if (opened) {
      // Cutting the link back to this window is what `noopener` would have
      // done — without throwing away the return value that tells us it
      // worked. Cross-origin makes this a no-op in some browsers, which is
      // why it is wrapped and why the result is not depended on.
      try {
        opened.opener = null;
      } catch (err) {
        /* nothing to sever */
      }
      return true;
    }
  } catch (err) {
    // No `window.open` at all, or it threw. Fall through.
  }

  /**
   * Nothing worked — almost always a pop-up blocker.
   *
   * This is where the old version navigated, and where it must not. Losing
   * the login screen is a far worse outcome than a link that did nothing:
   * the number is printed in full on screen, so somebody can still read it
   * onto their phone. Returning false lets a caller say so if it wants to.
   */
  return false;
}
